import React, { useState } from 'react'
import { useHomeStoryTree } from './HomeStoryTree.hooks'
import { getImageUrl } from 'utils/assets/Cdn'
import { SVG_DIMENSIONS, HOTSPOT_POSITIONS } from './HomeStoryTree.constants'
import LoadingColors from 'components/loading-colors'
import { useTranslation } from 'i18n'

const HomeStoryTree = () => {
  const {
    isLoading,
    loadingError,
    handleHotspotClick,
    hotspots,
    isLargeScreen,
    containerRef,
    getAdjustedPosition,
    getAdjustedWidth,
    getAdjustedHeight
  } = useHomeStoryTree()
  type HoveredKey =
    | 'leaderboard'
    | 'journeys'
    | 'storyMaker'
    | 'library'
    | 'forest'
  const [hovered, setHovered] = useState<HoveredKey | null>(null)

  const { t } = useTranslation()

  const bgImage = isLargeScreen
    ? 'story-tree/background-story-tree.svg'
    : 'story-tree/background-story-tree-v1.svg'

  const dimensions = isLargeScreen ? SVG_DIMENSIONS.LARGE : SVG_DIMENSIONS.SMALL
  const aspectRatio = `${dimensions.width} / ${dimensions.height}`
  const maxWidth = `${dimensions.width}px`
  const maxHeight = `${dimensions.height}px`

  const positions = isLargeScreen
    ? HOTSPOT_POSITIONS.LARGE
    : HOTSPOT_POSITIONS.SMALL

  const getHotspotStyle = (hotspotId: string) => {
    const config = positions[hotspotId as keyof typeof positions]
    if (!config) return {}

    return {
      ...getAdjustedPosition(
        config.leftPercent,
        config.topPercent,
        dimensions.width,
        dimensions.height
      ),
      ...getAdjustedWidth(config.widthPercent, dimensions.width),
      ...getAdjustedHeight(config.heightPercent, dimensions.height)
    }
  }

  if (isLoading) {
    return (
      <div className="w-full h-[calc(100vh-70px)] flex flex-col items-center justify-center">
        <LoadingColors text={t('Carregando conteúdo')} />
      </div>
    )
  }

  if (loadingError) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-b from-[#95be31] via-[#6ab419] to-[#40aa01] z-[9999]">
        <p className="font-['Averta',sans-serif] text-2xl font-bold text-white text-center">
          {loadingError}
        </p>
      </div>
    )
  }

  return (
    <div className="w-full h-[calc(100vh-70px)] flex justify-center items-center overflow-hidden bg-[#347111]">
      <div
        ref={containerRef}
        className="relative w-full h-full overflow-hidden"
        style={{
          aspectRatio,
          ...(!isLargeScreen && { maxHeight, maxWidth })
        }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <img
            src={getImageUrl(bgImage)}
            alt=""
            className={`absolute inset-0 w-full h-full ${
              isLargeScreen ? 'object-cover' : 'object-contain'
            } object-center`}
          />

          <div
            className="absolute block min-[930px]:hidden"
            style={getHotspotStyle('hillBackground')}
          >
            <div className="relative w-full h-full">
              <img
                src={getImageUrl('story-tree/hill-background.svg')}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          <div className="absolute" style={getHotspotStyle('leaderboard')}>
            <img
              src={getImageUrl('story-tree/leaderboard.svg')}
              alt=""
              className="w-full"
              style={{
                opacity: hovered === 'leaderboard' ? 0 : 1,
                transition: 'opacity 240ms linear'
              }}
            />
            <img
              src={getImageUrl('story-tree/leaderboard-hover.svg')}
              alt=""
              className="absolute top-0 left-0 w-full"
              style={{
                opacity: hovered === 'leaderboard' ? 1 : 0,
                transition: 'opacity 240ms linear',
                mixBlendMode: 'normal'
              }}
            />
          </div>

          <div className="absolute" style={getHotspotStyle('journeys')}>
            <img
              src={getImageUrl('story-tree/journeys.svg')}
              alt=""
              className="w-full"
              style={{
                opacity: hovered === 'journeys' ? 0 : 1,
                transition: 'opacity 240ms linear'
              }}
            />
            <img
              src={getImageUrl('story-tree/journeys-hover.svg')}
              alt=""
              className="absolute top-0 left-0 w-full"
              style={{
                opacity: hovered === 'journeys' ? 1 : 0,
                transition: 'opacity 240ms linear',
                mixBlendMode: 'normal'
              }}
            />
          </div>

          <div className="absolute" style={getHotspotStyle('forest')}>
            <img
              src={getImageUrl('story-tree/forest.svg')}
              alt=""
              className="w-full"
              style={{
                opacity: hovered === 'forest' ? 0 : 1,
                transition: 'opacity 240ms linear'
              }}
            />
            <img
              src={getImageUrl('story-tree/forest-hover.svg')}
              alt=""
              className="absolute top-0 left-0 w-full"
              style={{
                opacity: hovered === 'forest' ? 1 : 0,
                transition: 'opacity 240ms linear',
                mixBlendMode: 'normal'
              }}
            />
          </div>

          <div className="absolute" style={getHotspotStyle('storyMaker')}>
            <img
              src={getImageUrl('story-tree/story-maker.svg')}
              alt=""
              className="w-full"
              style={{
                opacity: hovered === 'storyMaker' ? 0 : 1,
                transition: 'opacity 240ms linear'
              }}
            />
            <img
              src={getImageUrl('story-tree/story-maker-hover.svg')}
              alt=""
              className="absolute top-0 left-0 w-full"
              style={{
                opacity: hovered === 'storyMaker' ? 1 : 0,
                transition: 'opacity 240ms linear',
                mixBlendMode: 'normal'
              }}
            />
          </div>

          <div className="absolute" style={getHotspotStyle('library')}>
            <img
              src={getImageUrl('story-tree/library.svg')}
              alt=""
              className="w-full"
              style={{
                opacity: hovered === 'library' ? 0 : 1,
                transition: 'opacity 240ms linear'
              }}
            />
            <img
              src={getImageUrl('story-tree/library-hover.svg')}
              alt=""
              className="absolute top-0 left-0 w-full"
              style={{
                opacity: hovered === 'library' ? 1 : 0,
                transition: 'opacity 240ms linear',
                mixBlendMode: 'normal'
              }}
            />
          </div>

          {!isLargeScreen && (
            <>
              <div
                className="absolute hover:scale-105 active:scale-98"
                style={getHotspotStyle('myReadings')}
              >
                <img
                  src={getImageUrl('story-tree/my-readings.svg')}
                  alt=""
                  className="w-full h-full object-contain transition-transform duration-240 ease-in-out"
                />
              </div>

              <div className="absolute" style={getHotspotStyle('assignments')}>
                <img
                  src={getImageUrl('story-tree/assignments.svg')}
                  alt=""
                  className="w-full h-full object-contain transition-transform duration-240 ease-in-out"
                />
              </div>
            </>
          )}
        </div>

        <div className="absolute inset-0 z-1">
          {hotspots
            .filter(hotspot => {
              if (hotspot.id === 'myReadings' || hotspot.id === 'assignments') {
                return !isLargeScreen
              }
              return true
            })
            .map(hotspot => (
              <button
                key={hotspot.id}
                className={`absolute bg-transparent border-none cursor-pointer transition-transform duration-240 ease-in-out z-20 ${
                  ![
                    'leaderboard',
                    'journeys',
                    'storyMaker',
                    'library',
                    'forest'
                  ].includes(hotspot.id)
                    ? 'hover:scale-105 active:scale-98'
                    : 'mt-[30px] min-[930px]:mt-[100px]'
                } ${
                  hotspot.id === 'myReadings' || hotspot.id === 'assignments'
                    ? 'ml-[35px] !w-[100px]'
                    : ''
                }`}
                style={{
                  ...getHotspotStyle(hotspot.id),
                  zIndex: hotspot.zIndex
                }}
                onClick={() => handleHotspotClick(hotspot.id)}
                onMouseEnter={() => {
                  if (
                    [
                      'leaderboard',
                      'journeys',
                      'storyMaker',
                      'library',
                      'forest'
                    ].includes(hotspot.id)
                  ) {
                    setHovered(hotspot.id as HoveredKey)
                  }
                }}
                onMouseLeave={() => {
                  if (
                    [
                      'leaderboard',
                      'journeys',
                      'storyMaker',
                      'library',
                      'forest'
                    ].includes(hotspot.id)
                  ) {
                    setHovered(null)
                  }
                }}
                aria-label={hotspot.label}
              />
            ))}
        </div>
      </div>

      <div className="absolute bottom-0 right-0 hidden min-[930px]:block">
        <div className="relative w-full h-full">
          <button
            className="absolute top-[-20px] right-[240px] cursor-pointer w-[135px] h-[187px] hover:scale-105 active:scale-98 z-20 transition-transform duration-240 ease-in-out"
            onClick={() => handleHotspotClick('myReadings')}
          >
            <img
              src={getImageUrl('story-tree/my-readings.svg')}
              alt=""
              className=" w-[135px] h-[187px] object-contain"
            />
          </button>
          <button
            className="absolute top-[-20px] right-[90px] cursor-pointer w-[135px] h-[187px] hover:scale-105 active:scale-98 z-20 transition-transform duration-240 ease-in-out"
            onClick={() => handleHotspotClick('assignments')}
          >
            <img
              src={getImageUrl('story-tree/assignments.svg')}
              alt=""
              className=" w-[135px] h-[187px] object-contain"
            />
          </button>
          <img
            src={getImageUrl('story-tree/hill-background.svg')}
            alt=""
            className="w-full h-full object-contain"
          />
        </div>
      </div>
    </div>
  )
}

export default HomeStoryTree
